import React, { useState } from 'react'
import { ContentBlock } from '../../../types/api'
import { useAppStore } from '../../../store/useAppStore'
import './ContentRenderer.css'
import triangle from '../../../assets/images/triangle.png'
import KinescopePlayer from './KinescopePlayer'

interface ContentRendererProps {
  content: ContentBlock[]
}

interface ContainerState {
  [blockId: string]: boolean
}

interface SliderState {
  [blockId: string]: number
}

interface GroupedContent {
  container?: ContentBlock & { type: 'container' }
  blocks: ContentBlock[]
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ content }) => {
  const { user } = useAppStore()
  const [collapsedContainers, setCollapsedContainers] =
    useState<ContainerState>({})

  // Добавляем состояние для всех слайдеров
  const [sliderStates, setSliderStates] = useState<SliderState>({})

  const toggleContainer = (blockId: string) => {
    setCollapsedContainers(prev => ({
      ...prev,
      [blockId]: !prev[blockId],
    }))
  }

  // Функция для управления состоянием слайдера
  const setSliderState = (blockId: string, slideIndex: number) => {
    setSliderStates(prev => ({
      ...prev,
      [blockId]: slideIndex,
    }))
  }

  // Общая функция для декодирования HTML-сущностей
  const decodeHtmlEntities = (text: string) => {
    const textArea = document.createElement('textarea')
    textArea.innerHTML = text
    return textArea.value
  }

  // Функция для обработки ссылок - добавляет target="_blank" для открытия в новой вкладке
  const processLinksForNewTab = (text: string) => {
    return text.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
      // Проверяем, есть ли уже target в атрибутах
      if (attributes.includes('target=')) {
        // Заменяем существующий target на target="_blank"
        return match.replace(/target\s*=\s*["'][^"']*["']/gi, 'target="_blank"')
      } else {
        // Добавляем target="_blank" к существующим атрибутам
        return `<a ${attributes} target="_blank">`
      }
    })
  }

  // Группируем блоки по контейнерам
  const groupContentByContainers = (
    blocks: ContentBlock[]
  ): GroupedContent[] => {
    const grouped: GroupedContent[] = []
    let currentGroup: GroupedContent = { blocks: [] }

    blocks.forEach(block => {
      if (block.type === 'container') {
        // Сохраняем предыдущую группу если она не пустая
        if (currentGroup.blocks.length > 0 || currentGroup.container) {
          grouped.push(currentGroup)
        }
        // Начинаем новую группу с контейнером
        currentGroup = {
          container: block as ContentBlock & { type: 'container' },
          blocks: [],
        }
      } else {
        // Добавляем блок в текущую группу
        currentGroup.blocks.push(block)
      }
    })

    // Добавляем последнюю группу
    if (currentGroup.blocks.length > 0 || currentGroup.container) {
      grouped.push(currentGroup)
    }

    return grouped
  }

  const renderParagraph = (block: ContentBlock & { type: 'paragraph' }) => {
    const decodedText = decodeHtmlEntities(block.data.text)
    const processedText = processLinksForNewTab(decodedText)

    return (
      <div key={block.id} className="content-paragraph">
        <p
          className={`paragraph-${block.data.alignment}`}
          dangerouslySetInnerHTML={{ __html: processedText }}
        />
      </div>
    )
  }

  const renderList = (block: ContentBlock & { type: 'list' }) => {
    const ListComponent = block.data.style === 'ordered' ? 'ol' : 'ul'

    return (
      <div key={block.id} className="content-list">
        <ListComponent className={`list-${block.data.style}`}>
          {block.data.items.map((item, index) => {
            const decodedItem = decodeHtmlEntities(item)
            const processedItem = processLinksForNewTab(decodedItem)

            return (
              <li
                key={index}
                dangerouslySetInnerHTML={{ __html: processedItem }}
              />
            )
          })}
        </ListComponent>
      </div>
    )
  }

  const renderEmbed = (block: ContentBlock & { type: 'embed' }) => {
    // Добавляем watermark параметр с Telegram ID пользователя
    const addWatermarkToUrl = (url: string) => {
      if (!user?.id) return url

      const urlObj = new URL(url)
      urlObj.searchParams.set('watermark', user.id.toString())
      return urlObj.toString()
    }

    const videoUrl = addWatermarkToUrl(block.data.embed)

    // Проверяем, является ли это Kinescope видео
    const isKinescopeVideo =
      videoUrl.includes('kinescope.io') || videoUrl.includes('kinescope.ru')

    if (isKinescopeVideo) {
      return (
        <div key={block.id} className="content-embed">
          <KinescopePlayer
            src={videoUrl}
            height={block.data.height}
            caption={block.data.caption}
            blockId={block.id}
          />
        </div>
      )
    }

    // Для других видео используем стандартный iframe
    return (
      <div key={block.id} className="content-embed">
        <div className="embed-wrapper">
          <iframe
            src={videoUrl}
            width="100%"
            height={block.data.height}
            frameBorder="0"
            // ключевое — разрешить фулскрин через Permissions-Policy
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            // дублируем совместимость
            allowFullScreen
            sandbox="allow-scripts allow-same-origin"
            title={`Видео ${block.id}`}
          />
        </div>
        {block.data.caption && (
          <p className="embed-caption">{block.data.caption}</p>
        )}
      </div>
    )
  }

  const renderHeader = (block: ContentBlock & { type: 'header' }) => {
    const HeaderTag = `h${block.data.level}` as keyof JSX.IntrinsicElements

    return (
      <div key={block.id} className="content-header">
        <HeaderTag className={`header-level-${block.data.level}`}>
          {decodeHtmlEntities(block.data.text)}
        </HeaderTag>
      </div>
    )
  }

  const renderSliderTool = (block: ContentBlock & { type: 'slidertool' }) => {
    const currentSlide = sliderStates[block.id] || 0

    if (block.data.displayMode === 'grid') {
      const isSingleImage = block.data.slides.length === 1

      return (
        <div key={block.id} className="content-slider-grid">
          <div className={`slider-grid ${isSingleImage ? 'single-image' : ''}`}>
            {block.data.slides.map((slide, index) => (
              <div key={index} className="grid-item">
                <img src={slide.url} alt={slide.caption} />
                {slide.caption && (
                  <p className="slide-caption">{slide.caption}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div key={block.id} className="content-slider">
        <div className="slider-wrapper">
          <div className="slider-container-page">
            <img
              src={block.data.slides[currentSlide]?.url}
              alt={block.data.slides[currentSlide]?.caption}
              className="slider-image"
            />
          </div>

          {block.data.slides.length > 1 && (
            <>
              <button
                className="slider-btn slider-prev"
                onClick={() => {
                  const newSlide =
                    currentSlide === 0
                      ? block.data.slides.length - 1
                      : currentSlide - 1
                  setSliderState(block.id, newSlide)
                }}
              >
                ‹
              </button>
              <button
                className="slider-btn slider-next"
                onClick={() => {
                  const newSlide =
                    currentSlide === block.data.slides.length - 1
                      ? 0
                      : currentSlide + 1
                  setSliderState(block.id, newSlide)
                }}
              >
                ›
              </button>

              <div className="slider-dots">
                {block.data.slides.map((_, index) => (
                  <button
                    key={index}
                    className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => setSliderState(block.id, index)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {block.data.slides[currentSlide]?.caption && (
          <p className="slide-caption">
            {block.data.slides[currentSlide].caption}
          </p>
        )}
      </div>
    )
  }

  const renderBlock = (block: ContentBlock) => {
    switch (block.type) {
      case 'paragraph':
        return renderParagraph(block as ContentBlock & { type: 'paragraph' })
      case 'list':
        return renderList(block as ContentBlock & { type: 'list' })
      case 'embed':
        return renderEmbed(block as ContentBlock & { type: 'embed' })
      case 'slidertool':
        return renderSliderTool(block as ContentBlock & { type: 'slidertool' })
      case 'header':
        return renderHeader(block as ContentBlock & { type: 'header' })
      default:
        return null
    }
  }

  const renderGroup = (group: GroupedContent, index: number) => {
    if (!group.container) {
      // Если нет контейнера, просто рендерим блоки
      return (
        <div key={`group-${index}`} className="content-group">
          {group.blocks.map(renderBlock)}
        </div>
      )
    }

    const container = group.container
    const isCollapsed =
      collapsedContainers[container.data.blockId] ?? container.data.collapsed
    const backgroundClass = `container-${container.data.background}`

    return (
      <div
        key={container.id}
        className={`content-container ${backgroundClass}`}
      >
        <div
          className={`container-header ${container.data.collapsible ? 'collapsible' : ''}`}
          onClick={
            container.data.collapsible
              ? () => toggleContainer(container.data.blockId)
              : undefined
          }
        >
          <h2 className="container-title">
            {decodeHtmlEntities(container.data.text)}
          </h2>
          {container.data.collapsible && (
            <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>
              <img
                src={triangle}
                alt="collapse"
                className="collapse-icon-img"
              />
            </span>
          )}
        </div>
        {(!container.data.collapsible || !isCollapsed) && (
          <div className="container-content">
            {group.blocks.map(renderBlock)}
          </div>
        )}
      </div>
    )
  }

  const groupedContent = groupContentByContainers(content)

  return (
    <div className="content-renderer">{groupedContent.map(renderGroup)}</div>
  )
}

export default ContentRenderer
